// ╔══════════════════════════════════════════════════════════════╗
// ║  Production History — entries log + filters + CSV export      ║
// ╚══════════════════════════════════════════════════════════════╝

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, downloadFile, getErrorMessage } from '../../lib/api.js';
import { useToast } from '../../components/Toast.jsx';
import { fmtAR, fmtDateShort, STATUS_LABEL_AR } from '../../lib/format.js';
import { Badge, Btn, Card, Field, Input, Pills, Select } from '../../components/ui.jsx';

const STATUS_TONES = {
  pending: 'warn',
  approved: 'success',
  rejected: 'danger',
};

function defaultFromDate() {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
}
function defaultToDate() {
  return new Date().toISOString().slice(0, 10);
}

export default function ProductionHistoryPage() {
  const toast = useToast();
  const [status, setStatus] = useState('all');
  const [deptId, setDeptId] = useState('all');
  const [from, setFrom] = useState(defaultFromDate());
  const [to, setTo] = useState(defaultToDate());
  const [page, setPage] = useState(1);

  const params = useMemo(
    () => ({
      page,
      pageSize: 30,
      ...(status !== 'all' && { status }),
      ...(deptId !== 'all' && { deptId }),
      ...(from && { from }),
      ...(to && { to }),
    }),
    [page, status, deptId, from, to],
  );

  const { data: deptsData } = useQuery({
    queryKey: ['departments'],
    queryFn: () => api.get('/departments').then((r) => r.data),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['production', 'entries', params],
    queryFn: () => api.get('/production/entries', { params }).then((r) => r.data),
  });

  const entries = data?.entries ?? [];
  const pagination = data?.pagination;
  const departments = deptsData?.departments ?? [];

  function handleExport() {
    const exportParams = {
      ...(status !== 'all' && { status }),
      ...(deptId !== 'all' && { deptId }),
      ...(from && { from }),
      ...(to && { to }),
    };
    const qs = new URLSearchParams(exportParams).toString();
    const filename = `production_history_${from || 'all'}_${to || 'all'}.csv`;
    downloadFile(`/production/entries/export?${qs}`, filename).catch((err) =>
      toast.push({ tone: 'danger', title: 'فشل التحميل', body: getErrorMessage(err) }),
    );
  }

  function resetFilters() {
    setStatus('all');
    setDeptId('all');
    setFrom(defaultFromDate());
    setTo(defaultToDate());
    setPage(1);
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 fade-up">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-brand-500 font-bold">
            الإنتاج
          </div>
          <h1 className="text-2xl md:text-3xl font-black mt-1">سجل الإدخالات</h1>
          {pagination && (
            <p className="text-sm text-ink-400 mt-1">
              {fmtAR(pagination.total)} سطر في النطاق المختار
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Btn variant="ghost" icon="x" onClick={resetFilters}>
            مسح الفلاتر
          </Btn>
          <Btn variant="primary" icon="download" onClick={handleExport}>
            تصدير Excel
          </Btn>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Field label="من تاريخ">
            <Input
              type="date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setPage(1);
              }}
            />
          </Field>
          <Field label="إلى تاريخ">
            <Input
              type="date"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setPage(1);
              }}
            />
          </Field>
          <Field label="الإدارة">
            <Select
              value={deptId}
              onChange={(e) => {
                setDeptId(e.target.value);
                setPage(1);
              }}
            >
              <option value="all">كل الإدارات</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.nameAr}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="حالة الإدخال">
            <Select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
            >
              <option value="all">الكل</option>
              <option value="pending">قيد المراجعة</option>
              <option value="approved">معتمد</option>
              <option value="rejected">مرفوض</option>
            </Select>
          </Field>
        </div>
        <Pills
          value={status}
          onChange={(v) => {
            setStatus(v);
            setPage(1);
          }}
          items={[
            { value: 'all', label: 'الكل' },
            { value: 'pending', label: 'قيد المراجعة' },
            { value: 'approved', label: 'معتمد' },
            { value: 'rejected', label: 'مرفوض' },
          ]}
        />
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 dark:bg-ink-800/40 text-xs text-ink-500">
              <tr>
                {['التاريخ', 'العامل', 'الإدارة', 'المرحلة', 'الهدف', 'الفعلي', 'الأثر', 'الحالة'].map(
                  (h, i) => (
                    <th key={i} className="text-start font-semibold px-4 py-3 whitespace-nowrap">
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="divide-soft">
              {isLoading && (
                <tr>
                  <td colSpan="8" className="p-8 text-center text-ink-400">
                    جاري التحميل…
                  </td>
                </tr>
              )}
              {!isLoading && entries.length === 0 && (
                <tr>
                  <td colSpan="8" className="p-8 text-center text-ink-400">
                    لا توجد إدخالات
                  </td>
                </tr>
              )}
              {entries.map((e) => (
                <tr key={e.id} className="hover:bg-ink-50 dark:hover:bg-ink-800/40">
                  <td className="px-4 py-3 font-mono text-xs">{fmtDateShort(e.date)}</td>
                  <td className="px-4 py-3 font-semibold">{e.workerName ?? e.workerId}</td>
                  <td className="px-4 py-3 text-ink-500 dark:text-ink-300">{e.deptId ?? '—'}</td>
                  <td className="px-4 py-3">{e.stageId}</td>
                  <td className="px-4 py-3 tabular">{fmtAR(e.target)}</td>
                  <td className="px-4 py-3 tabular font-extrabold">{fmtAR(e.actual)}</td>
                  <td className="px-4 py-3">
                    {Number(e.delta) > 0 ? (
                      <Badge tone="success">+{fmtAR(e.delta)} ج</Badge>
                    ) : Number(e.delta) < 0 ? (
                      <Badge tone="danger">{fmtAR(e.delta)} ج</Badge>
                    ) : (
                      <span className="text-ink-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={STATUS_TONES[e.status]} dot>
                      {STATUS_LABEL_AR[e.status]}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between p-4 text-xs text-ink-400 border-t border-ink-100 dark:border-ink-800">
            <span>
              {fmtAR(pagination.page)} من {fmtAR(pagination.totalPages)}
            </span>
            <div className="flex gap-1">
              <Btn variant="ghost" size="sm" icon="chevR" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={pagination.page <= 1}>السابق</Btn>
              <Btn variant="ghost" size="sm" iconEnd="chevL" onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))} disabled={pagination.page >= pagination.totalPages}>التالي</Btn>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
